import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import { Flex, asPercent } from 'lqx-react';
import { makeStyles } from '@material-ui/core';
import { CashLot, CashLotItem } from '../api/cash-lot';
import { getAggregatedCashLotItems } from './shared';

interface CashBalanceProps {
  cashLot: CashLot;
  frequency: string;
}

interface CashBalanceItem {
  restricted: number;
  nonRestricted: number;
  trend: number;
  dateUnit: string;
}

interface CashBalanceRegion {
  name: string;
  percentage: number;
}

interface CashBalanceAccountGroup{
  accountRefId: string;
  items : CashLotItem[];
}

const groupByAccountRefId = (items: CashLotItem[]): CashBalanceAccountGroup[] => {
  var result = items.reduce((r, v, i, a, k = v.accountRefId) => ((r[k] || (r[k] = [])).push(v), r), {});
  return Object.keys(result).map(x => { return { accountRefId: x, items: result[x] }; });
}

const getCashBalanceItems = (cashLot: CashLot, frequency: string): CashBalanceItem[] => {
  const aggregatedItems = getAggregatedCashLotItems(cashLot.items.filter(x => x.type === "Account"), frequency);
  const cashBalanceItems: CashBalanceItem[] = [];
  
  for (let aggregatedItem of aggregatedItems) {
    let accountGroups = groupByAccountRefId(aggregatedItem.items);
    let restricted = 0;
    let nonRestricted = 0;

    for (let accountGroup of accountGroups) {
      var cashLotItem = accountGroup.items.pop();
      switch (cashLotItem.subType) {
        case 'RESTRICTED': restricted += cashLotItem.amount; break;
        case 'NON_RESTRICTED': nonRestricted += cashLotItem.amount; break;
        default: break;
      }
    }

    cashBalanceItems.push({
      restricted,
      nonRestricted,
      trend: restricted + nonRestricted,
      dateUnit: aggregatedItem.dateUnit
    })
  }

  return cashBalanceItems;
};

const getHighchartOptions = (items: CashBalanceItem[]): Highcharts.Options => {
  return {
    chart: {
      height: 250
    },
    title: {
      text: ''
    },
    xAxis: {
      categories: items.map(x => x.dateUnit)
    },
    yAxis: {
      title: {
        text: ''
      }
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false
        }
      }
    },
    colors: ['#052C4C', '#3FB1E3', '#2e6586'],
    series: [
      {
        type: 'column',
        name: 'Non-Restricted',
        data: items.map(x => x.nonRestricted)
      },
      {
        type: 'column',
        name: 'Restricted',
        data: items.map(x => x.restricted)
      },      
      {
        type: 'spline',
        name: 'Trend',
        data: items.map(x => x.trend),
        color: Highcharts.getOptions().colors[3],
        marker: {
          lineWidth: 2,
          lineColor: Highcharts.getOptions().colors[3],
          fillColor: 'white'
        }
      }
    ],
    credits:{
      enabled: false
    }
  }
};

const getCashBalanceRegions = (cashLot: CashLot): CashBalanceRegion[] => {
  const regionsMap = new Map<string, number>();
  let totalAmount = 0;

  let accountGroups = groupByAccountRefId(cashLot.items.filter(x => x.type === "Account"));
  for (let account of accountGroups) {
    let latestAccoutFromGroup = account.items.pop();
    let accountRegion = !latestAccoutFromGroup.region ? 'Blank' : latestAccoutFromGroup.region;
    totalAmount += latestAccoutFromGroup.amount;

    if (!regionsMap.has(accountRegion)) {
      regionsMap.set(accountRegion, 0);
    }

    regionsMap.set(accountRegion, regionsMap.get(accountRegion) + latestAccoutFromGroup.amount);
  }

  return Array.from(regionsMap.entries())
    .map(([key, amount]) =>({ name: key, percentage: (amount / totalAmount)? (amount / totalAmount) : 0}))
    .concat(Array.from(new Array(4).keys()).map(() => ({ name: '', percentage: 0 })))
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 4);
};

export const CashBalance = ({ cashLot, frequency }: CashBalanceProps) => {
  const items = React.useMemo(() => getCashBalanceItems(cashLot, frequency), [cashLot.items['_changeToken'], frequency]);
  const regions = React.useMemo(() => getCashBalanceRegions(cashLot), [cashLot.items['_changeToken'], frequency]);

  const options = getHighchartOptions(items);

  return (
    <Flex row>
      <HighchartsReact highcharts={Highcharts} options={options} />
      <CashBalanceBreakdown regions={regions} />
    </Flex>
  )
};

const CashBalanceBreakdown = ({ regions }: { regions: CashBalanceRegion[] }) => {
  const classes = useCashBalanceBreakdownStyles();

  const getPercentageValue = (value: number): string => {
    if (value === 0) return '';
    return value*100 < 1 ? '<1%' : asPercent(value);
  };

  return (
    <div className={classes.root}>
      <CashBalanceCell value={getPercentageValue(regions[0].percentage)} label={regions[0].name} row={1} column={1} borderRight />
      <CashBalanceCell value={getPercentageValue(regions[1].percentage)} label={regions[1].name} row={1} column={2} />
      <CashBalanceCell value={getPercentageValue(regions[2].percentage)} label={regions[2].name} row={2} column={1} borderRight borderTop />
      <CashBalanceCell value={getPercentageValue(regions[3].percentage)} label={regions[3].name} row={2} column={2} borderTop />
    </div>
  );
};

const useCashBalanceBreakdownStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateRows: '50% 50%',
    gridTemplateColumns: '50% 50%',
    width: '200px',
    height: '200px',
    alignSelf: 'center'
  }
});

interface CashBalanceCellProps {
  value: string;
  label: string;
  borderRight?: boolean;
  borderTop?: boolean;
  row: number;
  column: number;
}

const useCashBalanceCellStyles = makeStyles({
  cell: {
    borderRight: (props: CashBalanceCellProps) => props.borderRight ? '2px solid #dcdfe0' : 'none',
    borderTop: (props: CashBalanceCellProps) => props.borderTop ? '2px solid #dcdfe0' : 'none',
    display: 'flex',
    flexDirection: 'column',
    gridRowStart: (props: CashBalanceCellProps) => props.row,
    gridRowEnd: (props: CashBalanceCellProps) => props.row,
    gridColumnStart: (props: CashBalanceCellProps) => props.column,
    gridColumnEnd: (props: CashBalanceCellProps) => props.column,
    alignItems: 'center',
    justifyContent: 'center'
  },
  value: {
    fontWeight: 500,
    fontSize: '24px'
  }
});

const CashBalanceCell = (props: CashBalanceCellProps) => {
  const classes = useCashBalanceCellStyles(props);

  return (
    <div className={classes.cell}>
      <div className={classes.value}>{props.value}</div>
      <div>{props.label}</div>
    </div>
  );
}